import OpenInNewIcon from '@material-design-icons/svg/filled/open_in_new.svg?react';
import { SelectFilter } from '../../../components/common/FilterBar';
import { formatDate, formatFileSize } from '../../../utils/stringHelpers';
import {
  formatReportingLifecycleLabel,
  formatNullableCount,
  formatReviewScore,
  type ReportingLifecycleFilterValue,
  sortReportingAppsAlphabetically,
} from '../reportingUtils';
import type {
  AdminReportingAppDetailOutput,
  ReportingApp,
  ReportingStoreListing,
} from '../types';
import {
  LifetimeTag,
  RatingDistributionBar,
  RatingStars,
  ReportingInset,
  ReportingPanel,
  ReportingSectionHeader,
  ReportingStatusPill,
} from './ReportingChrome';
import { ReportingAppIcon } from './ReportingAppIcon';
import { ReportingMetricCard } from './ReportingMetricCard';

interface IndividualAppSectionProps {
  apps: ReportingApp[];
  selectedAppId: string | null;
  onSelectApp: (appId: string) => void;
  lifecycleOptions: Array<{
    value: ReportingLifecycleFilterValue;
    label: string;
  }>;
  lifecycleValue: ReportingLifecycleFilterValue;
  onLifecycleChange: (value: ReportingLifecycleFilterValue) => void;
  emptyMessage: string;
  selectedAppDetail: AdminReportingAppDetailOutput | null;
  selectedAppDetailError: string | null;
  isSelectedAppDetailLoading: boolean;
}

export function IndividualAppSection({
  apps,
  selectedAppId,
  onSelectApp,
  lifecycleOptions,
  lifecycleValue,
  onLifecycleChange,
  emptyMessage,
  selectedAppDetail,
  selectedAppDetailError,
  isSelectedAppDetailLoading,
}: IndividualAppSectionProps) {
  const sortedApps = sortReportingAppsAlphabetically(apps);
  const selectedApp =
    sortedApps.find((app) => app.dappId === selectedAppId) ??
    sortedApps[0] ??
    null;

  if (!selectedApp) {
    return (
      <ReportingPanel>
        <div className="px-6 py-12 text-center text-sm text-neutral-500">
          {emptyMessage}
        </div>
      </ReportingPanel>
    );
  }

  const storeListing: ReportingStoreListing | null =
    selectedAppDetail?.storeListing ?? selectedApp.storeListing;
  const storePrimaryCategory =
    selectedAppDetail?.storePrimaryCategory ?? selectedApp.storePrimaryCategory;

  return (
    <div className="space-y-4">
      <ReportingPanel className="overflow-hidden">
        <div className="relative p-5">
          {storeListing?.bannerUrl && (
            <div
              className="absolute inset-x-0 top-0 h-32 opacity-30"
              style={{
                backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(15,15,15,1) 90%), url(${storeListing.bannerUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
          )}

          <div className="relative flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-start gap-4">
              <ReportingAppIcon
                app={{ ...selectedApp, storeListing }}
                alt={`${selectedApp.dappName} icon`}
                loading="eager"
                size="lg"
              />
              <div className="min-w-0">
                <div className="text-lg font-semibold tracking-tight text-white">
                  {selectedApp.dappName}
                </div>
                <div className="mt-0.5 truncate text-sm text-neutral-500">
                  {selectedApp.packageName || 'No Android package'}
                  {storePrimaryCategory && ` · ${storePrimaryCategory}`}
                </div>

                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  <ReportingStatusPill label={selectedApp.category} />
                  <ReportingStatusPill label={selectedApp.platformType} />
                  <ReportingStatusPill
                    label={formatReportingLifecycleLabel(selectedApp.lifecycle)}
                    tone={selectedApp.lifecycle === 'new' ? 'teal' : 'neutral'}
                  />
                  <ReportingStatusPill
                    label={storeListing ? 'Live listing' : 'No listing'}
                    tone={storeListing ? 'teal' : 'amber'}
                  />
                  {storeListing?.auxStatus && (
                    <ReportingStatusPill
                      label={storeListing.auxStatus}
                      tone="amber"
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col items-stretch gap-2 sm:flex-row lg:flex-col lg:items-end xl:flex-row xl:items-center">
              <SelectFilter
                value={lifecycleValue}
                onChange={(value) =>
                  onLifecycleChange(value as ReportingLifecycleFilterValue)
                }
                options={lifecycleOptions}
                className="rounded-lg border-neutral-800/50 bg-neutral-950/60"
              />
              <select
                value={selectedApp.dappId}
                onChange={(event) => onSelectApp(event.target.value)}
                className="rounded-lg border border-neutral-800/50 bg-neutral-950/60 px-3 py-2 text-sm text-white [color-scheme:dark] focus:outline-none focus:ring-1 focus:ring-teal-500/50 sm:min-w-[16rem] lg:w-64"
              >
                {sortedApps.map((app) => (
                  <option key={app.dappId} value={app.dappId}>
                    {app.dappName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="relative mt-4 grid gap-2 border-t border-neutral-800/70 pt-4 sm:grid-cols-3">
            <TimelineMarker
              label="First signal"
              date={selectedApp.firstPublishedAt}
            />
            <TimelineMarker
              label="Latest signal"
              date={selectedApp.latestPublishedAt}
            />
            <TimelineMarker
              label="Store updated"
              date={storeListing?.updatedOn ?? null}
            />
          </div>
        </div>
      </ReportingPanel>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <ReportingMetricCard
          label="Current Version"
          value={
            storeListing?.versionName
              ? `v${storeListing.versionName}`
              : 'No listing'
          }
          hint={
            storeListing?.versionCode
              ? `Code ${storeListing.versionCode}`
              : 'Requires a live store listing'
          }
          tone={storeListing ? 'teal' : 'default'}
          emphasis="highlight"
        />
        <ReportingMetricCard
          label="App Size"
          value={
            (storeListing?.installSizeBytes ?? selectedApp.appSizeBytes) !==
            null
              ? formatFileSize(
                  storeListing?.installSizeBytes ?? selectedApp.appSizeBytes,
                )
              : 'Unknown'
          }
          hint={
            storeListing?.installSizeBytes
              ? 'From live store listing'
              : 'From portal release'
          }
        />
        <ReportingMetricCard
          label="Updates In Range"
          value={selectedApp.updatesInPeriod.toLocaleString()}
          hint={`${selectedApp.totalPublishedReleases.toLocaleString()} total publish signals`}
          tone="teal"
        />
        <ReportingMetricCard
          label="Reviews"
          value={formatNullableCount(selectedApp.reviews)}
          trailing={selectedApp.reviews !== null ? <LifetimeTag /> : undefined}
          hint={
            selectedApp.reviews === null
              ? 'Requires a live store listing'
              : formatReviewScore(
                    selectedApp.averageReviewScore,
                    selectedApp.reviews,
                  ) === 'No ratings'
                ? 'No public ratings yet'
                : 'All-time public dapp store reviews'
          }
          tone={
            selectedApp.reviews === null
              ? 'amber'
              : (selectedApp.averageReviewScore ?? 0) >= 4
                ? 'teal'
                : 'default'
          }
          muted={selectedApp.reviews === null}
          emphasis="highlight"
          footer={
            selectedApp.reviews !== null && selectedApp.reviews > 0 ? (
              <div className="flex items-center justify-between gap-2">
                <RatingStars
                  value={selectedApp.averageReviewScore}
                  showValue
                  size="md"
                />
              </div>
            ) : undefined
          }
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
        <ReportingPanel className="overflow-hidden">
          <div className="space-y-4 p-5">
            <ReportingSectionHeader
              title="Live store metadata"
              subtitle="Pulled from the live dapp store listing"
            />

            {isSelectedAppDetailLoading && (
              <div className="text-sm text-neutral-500">
                Loading store data…
              </div>
            )}

            {!isSelectedAppDetailLoading && storeListing ? (
              <>
                <div className="grid gap-3 sm:grid-cols-2">
                  <DetailBlock
                    label="Publisher"
                    value={storeListing.publisherName ?? 'Unknown'}
                    secondary={
                      storeListing.publisherWebsite ??
                      storeListing.publisherSupportEmail ??
                      storeListing.publisherContact ??
                      null
                    }
                  />
                  <DetailBlock
                    label="Android"
                    value={
                      storeListing.minSdk
                        ? `Min SDK ${storeListing.minSdk}`
                        : 'SDK unavailable'
                    }
                    secondary={
                      storeListing.certFingerprint
                        ? `Cert ${storeListing.certFingerprint.slice(0, 16)}…`
                        : null
                    }
                  />
                  <DetailBlock
                    label="Media"
                    value={`${storeListing.screenshotCount} screenshots`}
                    secondary={
                      storeListing.ageRating
                        ? `Age rating ${storeListing.ageRating}`
                        : null
                    }
                  />
                  <DetailBlock
                    label="Localization"
                    value={`${storeListing.languageCount} languages · ${storeListing.permissionCount} permissions`}
                    secondary={null}
                  />
                </div>

                {storeListing.description && (
                  <TextBlock
                    label="Description"
                    value={storeListing.description}
                  />
                )}
                {storeListing.newInVersion && (
                  <TextBlock
                    label="What's new"
                    value={storeListing.newInVersion}
                  />
                )}
                {storeListing.sagaFeatures && (
                  <TextBlock
                    label="Saga features"
                    value={storeListing.sagaFeatures}
                  />
                )}

                <LinkListBlock links={buildStoreLinks(storeListing)} />
                <MediaPreviewSection storeListing={storeListing} />
              </>
            ) : (
              !isSelectedAppDetailLoading && (
                <div className="text-sm text-neutral-500">
                  {selectedAppDetailError ??
                    'No live store listing matched this app.'}
                </div>
              )
            )}
          </div>
        </ReportingPanel>

        <div className="space-y-4">
          <ReportingPanel className="overflow-hidden">
            <div className="space-y-4 p-5">
              <ReportingSectionHeader
                title="Rating distribution"
                trailing={
                  selectedApp.reviews !== null && selectedApp.reviews > 0 ? (
                    <RatingStars
                      value={selectedApp.averageReviewScore}
                      showValue
                      size="sm"
                    />
                  ) : undefined
                }
              />
              {selectedApp.reviews !== null && selectedApp.reviews > 0 ? (
                <RatingDistributionBar
                  buckets={{
                    one: selectedApp.oneStarReviews,
                    two: selectedApp.twoStarReviews,
                    three: selectedApp.threeStarReviews,
                    four: selectedApp.fourStarReviews,
                    five: selectedApp.fiveStarReviews,
                  }}
                  variant="detailed"
                />
              ) : (
                <div className="text-sm text-neutral-500">
                  {selectedApp.reviews === null
                    ? 'Requires a live store listing.'
                    : 'No public ratings yet.'}
                </div>
              )}
            </div>
          </ReportingPanel>

          <ReportingPanel className="overflow-hidden">
            <div className="space-y-3 p-5">
              <ReportingSectionHeader
                title="Recent reviews"
                subtitle={
                  storeListing?.hasMoreReviews
                    ? 'More reviews exist in the live store feed'
                    : undefined
                }
              />

              {isSelectedAppDetailLoading && (
                <div className="text-sm text-neutral-500">Loading…</div>
              )}

              {!isSelectedAppDetailLoading &&
              storeListing?.recentReviews.length ? (
                <ul className="space-y-2">
                  {storeListing.recentReviews.map((review) => (
                    <li key={review.id}>
                      <ReportingInset className="p-3">
                        <div className="flex items-center justify-between gap-2">
                          <RatingStars value={review.rating} size="sm" />
                          <span className="text-xs text-neutral-600">
                            {formatDate(review.createdAt)}
                          </span>
                        </div>
                        {review.review && (
                          <div className="mt-2 text-sm leading-relaxed text-neutral-300">
                            {review.review}
                          </div>
                        )}
                        <div className="mt-2 text-xs text-neutral-600">
                          {review.domain || review.walletAddress || 'Anonymous'}
                        </div>
                      </ReportingInset>
                    </li>
                  ))}
                </ul>
              ) : (
                !isSelectedAppDetailLoading && (
                  <div className="text-sm text-neutral-500">
                    {selectedApp.reviews === null
                      ? 'Requires a live store listing.'
                      : 'No recent reviews.'}
                  </div>
                )
              )}
            </div>
          </ReportingPanel>
        </div>
      </div>
    </div>
  );
}

function TimelineMarker({
  label,
  date,
}: {
  label: string;
  date: number | null;
}) {
  return (
    <div className="rounded-md border border-neutral-800/70 bg-neutral-950/40 p-2.5">
      <div className="text-[11px] uppercase tracking-wider text-neutral-500">
        {label}
      </div>
      <div className="mt-0.5 text-sm font-medium tabular-nums text-neutral-200">
        {date ? formatDate(date) : 'Unknown'}
      </div>
    </div>
  );
}

function DetailBlock({
  label,
  value,
  secondary,
}: {
  label: string;
  value: string;
  secondary: string | null;
}) {
  return (
    <ReportingInset className="p-3">
      <div className="text-[11px] uppercase tracking-wider text-neutral-500">
        {label}
      </div>
      <div className="mt-0.5 text-sm font-medium text-neutral-200">{value}</div>
      {secondary && (
        <div className="mt-1 truncate text-xs text-neutral-500">
          {secondary}
        </div>
      )}
    </ReportingInset>
  );
}

function TextBlock({ label, value }: { label: string; value: string }) {
  return (
    <ReportingInset className="p-3">
      <div className="text-[11px] uppercase tracking-wider text-neutral-500">
        {label}
      </div>
      <div className="mt-1 max-h-32 overflow-y-auto whitespace-pre-wrap text-sm leading-relaxed text-neutral-300">
        {value}
      </div>
    </ReportingInset>
  );
}

function LinkListBlock({
  links,
}: {
  links: Array<{ label: string; url: string }>;
}) {
  if (links.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {links.map((link) => (
        <a
          key={`${link.label}:${link.url}`}
          href={link.url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 rounded-full border border-neutral-700/50 bg-neutral-900/50 px-2.5 py-1 text-xs text-teal-300 transition hover:border-teal-700/50 hover:text-teal-200"
        >
          <span>{link.label}</span>
          <OpenInNewIcon className="size-3 fill-current" />
        </a>
      ))}
    </div>
  );
}

function MediaPreviewSection({
  storeListing,
}: {
  storeListing: ReportingStoreListing;
}) {
  const heroImages = [
    { label: 'Feature Graphic', url: storeListing.featureGraphicUrl },
    { label: 'Banner', url: storeListing.bannerUrl },
  ].filter((asset): asset is { label: string; url: string } =>
    Boolean(asset.url),
  );

  if (heroImages.length === 0 && storeListing.screenshotUrls.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      {heroImages.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2">
          {heroImages.map((asset) => (
            <ReportingInset key={asset.label} className="overflow-hidden">
              <img
                src={asset.url}
                alt={asset.label}
                className="h-36 w-full object-cover"
                loading="lazy"
                decoding="async"
              />
              <div className="border-t border-neutral-800/40 px-3 py-2 text-xs text-neutral-500">
                {asset.label}
              </div>
            </ReportingInset>
          ))}
        </div>
      )}

      {storeListing.screenshotUrls.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {storeListing.screenshotUrls.map((url, index) => (
            <ReportingInset key={`${url}:${index}`} className="overflow-hidden">
              <img
                src={url}
                alt={`Screenshot ${index + 1}`}
                className="h-48 w-full object-cover"
                loading="lazy"
                decoding="async"
              />
              <div className="border-t border-neutral-800/40 px-3 py-2 text-xs text-neutral-500">
                Screenshot {index + 1}
              </div>
            </ReportingInset>
          ))}
        </div>
      )}
    </div>
  );
}

function buildStoreLinks(storeListing: ReportingStoreListing) {
  return [
    { label: 'Publisher site', url: storeListing.publisherWebsite },
    { label: 'Privacy policy', url: storeListing.privacyPolicyUrl },
    { label: 'License', url: storeListing.licenseUrl },
    { label: 'Copyright', url: storeListing.copyrightUrl },
    { label: 'Install file', url: storeListing.installUrl },
  ].filter((link): link is { label: string; url: string } => Boolean(link.url));
}
